import { useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import { FinanceCard } from "@/components/cards/FinanceCard";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TransactionTable } from "@/components/transactions/TransactionTable";
import { AccountManager } from "@/components/management/AccountManager";
import { lazy, Suspense } from "react";
const DashboardCharts = lazy(() => import("@/components/dashboard/DashboardCharts").then(module => ({ default: module.DashboardCharts })));
import { AppSidebar } from "@/components/layout/AppSidebar";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { UserProfileHeader } from "@/components/common/UserProfileHeader";
import { MobileDashboardHeader } from "@/components/layout/MobileDashboardHeader";
import { MobileSummaryCards } from "@/components/dashboard/MobileSummaryCards";
import GoalsPage from "@/pages/GoalsPage";
import { Transaction, Meta, Conta, Category } from "@/types/database";
import { CategoryManager } from "@/components/management/CategoryManager";
import { Reports } from "@/pages/Reports";
import Accounts from "@/pages/Accounts";
import Categories from "@/pages/Categories";
import { MonthYearPicker } from "@/components/common/MonthYearPicker";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import * as Icons from "lucide-react";
import { TrendingUp, TrendingDown } from "lucide-react";

import { CreditCardManager } from "@/components/management/CreditCardManager";
import { DesktopTopbar } from "@/components/layout/DesktopTopbar";
import { useDataRefresh } from "@/contexts/DataRefreshContext";
import { formatCurrency, getEffectiveTransactionDate } from "@/lib/utils";
import { differenceInMonths } from "date-fns";
import { LocalAIAssistant } from "@/components/assistant/LocalAIAssistant";
import { MicPermissionModal } from "@/components/assistant/MicPermissionModal";


const Index = () => {
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Conta[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [mainGoal, setMainGoal] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentTab, setCurrentTab] = useState("dashboard");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [typeFilter, setTypeFilter] = useState<'todos' | 'entrada' | 'saida'>('todos');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [reportsInitialActive, setReportsInitialActive] = useState<
    | 'monthly'
    | 'balance'
    | 'pending'
    | 'category-expenses'
    | 'category-income'
    | 'monthly-expenses'
    | 'monthly-income'
    | 'yearly-comparison'
    | 'monthly-balance'
    | 'cash-flow'
    | 'net-worth'
    | 'trends-analysis'
    | 'yearly-income'
    | undefined
  >(undefined);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { refreshTrigger } = useDataRefresh();

  // Ler o state da navegação (React Router location state) para selecionar a aba correta
  useEffect(() => {
    const state = location.state as { tab?: string } | null;
    
    if (state?.tab && ['dashboard', 'transactions', 'reports', 'credit-cards'].includes(state.tab)) {
      setCurrentTab(state.tab);
    }
  }, [location.state]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  const fetchAllTransactions = useCallback(async () => {
    try {
      if (!user?.id) return;

      const { data: paidInvoices } = await supabase
        .from("faturas_cartao")
        .select("cartao_id, mes_referencia, ano_referencia, data_pagamento")
        .eq("paga", true);

      const { data: contasData, error: contasError } = await supabase
        .from("contas")
        .select("id, tipo, dia_fechamento, dia_vencimento")
        .eq("user_id", user.id)
        .eq("ativo", true);

      if (contasError) throw contasError;

      const accountsById = new Map(
        (contasData || []).map((conta) => [conta.id, conta])
      );

      const { data: allData, error: allError } = await supabase.from("transacoes").select(`
          *,
          categoria:categorias(*)
        `)
        .eq('ignorar_transacao', false)
        .order("data", { ascending: false });

      if (allError) throw allError;

      const checkInvoicePaid = (t: Transaction) => {
        if (!paidInvoices || !t.data || t.tipo !== "saida" || !t.conta_id) {
          return { foi_paga: t.foi_paga, data_pagamento: t.data_pagamento };
        }

        const account = accountsById.get(t.conta_id);
        if (!account || account.tipo !== "cartao_credito") {
          return { foi_paga: t.foi_paga, data_pagamento: t.data_pagamento };
        }

        try {
          const parts = String(t.data).split("T")[0].split("-").map(Number);
          if (parts.length !== 3 || parts.some(Number.isNaN)) {
            return { foi_paga: t.foi_paga, data_pagamento: t.data_pagamento };
          }

          const transactionDate = new Date(parts[0], parts[1] - 1, parts[2]);
          let mes = transactionDate.getMonth() + 1;
          let ano = transactionDate.getFullYear();
          const ultimoDiaMes = (y: number, m: number) => new Date(y, m, 0).getDate();

          if (account.dia_fechamento != null) {
            const diaFechamentoAjustado = Math.min(account.dia_fechamento, ultimoDiaMes(ano, mes));
            const dataFechamento = new Date(ano, mes - 1, diaFechamentoAjustado);
            if (transactionDate > dataFechamento) {
              if (mes === 12) {
                mes = 1;
                ano += 1;
              } else {
                mes += 1;
              }
            }
          } else if (account.dia_vencimento != null) {
            const diaVencimentoAjustado = Math.min(account.dia_vencimento, ultimoDiaMes(ano, mes));
            const dataVencimento = new Date(ano, mes - 1, diaVencimentoAjustado);
            if (transactionDate > dataVencimento) {
              if (mes === 12) {
                mes = 1;
                ano += 1;
              } else {
                mes += 1;
              }
            }
          }

          const invoice = paidInvoices.find((inv) =>
            inv.cartao_id === t.conta_id &&
            inv.mes_referencia === mes &&
            inv.ano_referencia === ano
          );

          if (!invoice) {
            return { foi_paga: t.foi_paga, data_pagamento: t.data_pagamento };
          }

          return {
            foi_paga: true,
            data_pagamento: t.data_pagamento || invoice.data_pagamento
          };
        } catch {
          return { foi_paga: t.foi_paga, data_pagamento: t.data_pagamento };
        }
      };

      const actualTransactions: Transaction[] = (allData || []).map(t => {
        const syncStatus = checkInvoicePaid(t);
        return {
          ...t,
          tipo: t.tipo as 'entrada' | 'saida',
          foi_paga: syncStatus.foi_paga,
          data_pagamento: syncStatus.data_pagamento,
          frequencia_repeticao: (t.frequencia_repeticao ?? undefined) as 'dias' | 'semanas' | 'meses' | 'anos' | undefined,
        };
      });

      const allExpandedTransactions: Transaction[] = actualTransactions;
      allExpandedTransactions.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());

      // Extract unique categories from transactions
      const uniqueCategories = Array.from(new Map(
        allExpandedTransactions
          .filter(t => t.categoria)
          .map(t => [t.categoria!.id, t.categoria!])
      ).values());

      setCategories(uniqueCategories);
      setAllTransactions(allExpandedTransactions);
    } catch (error) {
      console.error("Erro ao carregar todas as transações:", error);
      toast.error("Erro ao carregar dados para gráficos");
    }
  }, [user?.id]);

  const fetchTransactions = useCallback(async (dateFilter?: Date, typeFilterParam?: 'todos' | 'entrada' | 'saida') => {
    // Agora que usamos allTransactions para tudo, não precisamos mais buscar transações por mês no DB.
    // Em vez disso, apenas garantimos que allTransactions esteja atualizado.
    try {
      await fetchAllTransactions();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchAllTransactions]);

  const fetchAccountsBalance = useCallback(async () => {
    try {
      if (!user?.id) {
        setTotalBalance(0);
        setAccountsCount(0);
        setAccounts([]);
        return;
      }
      const { data: contas, error } = await supabase
        .from("contas")
        .select("*")
        .eq("user_id", user.id)
        .eq("ativo", true);

      if (error) throw error;

      // Calcular o saldo total de todas as contas (incluindo cartões de crédito)
      const total = (contas || []).reduce((sum, conta) => {
        return sum + (conta.saldo_atual || 0);
      }, 0);

      // Separar contas normais de cartões de crédito apenas para contagem
      const contasNormais = (contas || []).filter(conta => conta.tipo !== "cartao_credito");

      setTotalBalance(total);
      setAccountsCount(contasNormais.length);
      setAccounts((contas || []).map(c => ({
        ...c,
        // Ajustar tipo para union esperado em Conta
        tipo: c.tipo as 'cartao_credito' | 'carteira' | 'conta_corrente' | 'poupanca',
      })));
    } catch (error) {
      console.error("Erro ao carregar saldo total das contas:", error);
      setTotalBalance(0);
      setAccountsCount(0);
      setAccounts([]);
    }
  }, [user?.id]);

  const fetchCreditCardsData = useCallback(async () => {
    try {
      if (!user?.id) {
        setCreditCardsData({ totalLimit: 0, totalUsed: 0, cardsCount: 0 });
        return;
      }
      const { data: creditCards, error } = await supabase
        .from("contas")
        .select("limite_credito, fatura_atual")
        .eq("tipo", "cartao_credito")
        .eq("user_id", user.id)
        .eq("ativo", true);

      if (error) throw error;

      const totalLimit = (creditCards || []).reduce((sum, card) => sum + (card.limite_credito || 0), 0);
      const totalUsed = (creditCards || []).reduce((sum, card) => sum + (card.fatura_atual || 0), 0);
      const cardsCount = (creditCards || []).length;

      setCreditCardsData({ totalLimit, totalUsed, cardsCount });
    } catch (error) {
      console.error("Erro ao carregar dados dos cartões de crédito:", error);
      setCreditCardsData({ totalLimit: 0, totalUsed: 0, cardsCount: 0 });
    }
  }, [user?.id]);

  const fetchMainGoal = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('metas')
        .select('*')
        .eq('meta_principal', true)
        .limit(1);

      if (error) throw error;
      setMainGoal(data && data.length > 0 ? data[0] : null);
    } catch (error) {
      console.error('Error fetching main goal:', error);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchTransactions(selectedDate, typeFilter), fetchAllTransactions(), fetchMainGoal(), fetchAccountsBalance(), fetchCreditCardsData()]);
  }, [fetchTransactions, fetchAllTransactions, fetchMainGoal, fetchAccountsBalance, fetchCreditCardsData, selectedDate, typeFilter]);

  const handleDateChange = (newDate: Date) => {
    setSelectedDate(newDate);
    fetchTransactions(newDate, typeFilter);
  };

  const handleTabChange = (tab: string) => {
    // Limpar filtros de busca ao navegar entre abas
    setSearchQuery("");
    setSearchOpen(false);

    setCurrentTab(tab);
    if (tab === "dashboard" || tab === "transactions") {
      if (tab === "dashboard") {
        setTypeFilter("todos");
      }
      // Carregamento automático completo quando entra no dashboard ou transações
      handleRefresh();
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchAllTransactions();
    fetchMainGoal();
    fetchAccountsBalance();
    fetchCreditCardsData();
  }, [fetchTransactions, fetchAllTransactions, fetchMainGoal, fetchAccountsBalance, fetchCreditCardsData]);

  // Reagir ao contexto de refresh global
  useEffect(() => {
    if (refreshTrigger > 0) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);

  // Carregamento automático quando a aba atual muda para dashboard ou transactions
  useEffect(() => {
    if (currentTab === "dashboard" || currentTab === "transactions") {
      handleRefresh();
    }
  }, [currentTab, handleRefresh]);

  // Atualização automática periódica dos dados quando no dashboard
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (currentTab === "dashboard") {
      // Atualiza os dados a cada 30 segundos quando no dashboard
      intervalId = setInterval(() => {
        handleRefresh();
      }, 30000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentTab, handleRefresh]);

  const [totalBalance, setTotalBalance] = useState<number>(0);
  const [accountsCount, setAccountsCount] = useState<number>(0);
  const [creditCardsData, setCreditCardsData] = useState({ totalLimit: 0, totalUsed: 0, cardsCount: 0 });

  const calculateTotals = () => {
    const income = allTransactions.filter(t => {
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
      return t.tipo === 'entrada' && !t.ignorar_transacao && 
             transactionDate.getMonth() === selectedDate.getMonth() && 
             transactionDate.getFullYear() === selectedDate.getFullYear();
    }).reduce((sum, t) => sum + Number(t.valor), 0);

    const expenses = allTransactions.filter(t => {
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
      return t.tipo === 'saida' && !t.ignorar_transacao && 
             transactionDate.getMonth() === selectedDate.getMonth() && 
             transactionDate.getFullYear() === selectedDate.getFullYear();
    }).reduce((sum, t) => sum + Number(t.valor), 0);

    return { income, expenses };
  };

  const { income, expenses } = calculateTotals();

  const pendingSumThisMonth = useMemo(() => {
    const m = selectedDate.getMonth();
    const y = selectedDate.getFullYear();
    return (allTransactions || [])
      .filter((t) => t.tipo === 'saida' && !t.foi_paga && !String(t.id).startsWith('virtual-'))
      .filter((t) => {
        if (t.conta_id) {
          const account = accounts.find(a => a.id === t.conta_id);
          if (account && account.tipo === 'cartao_credito') return false;
        }
        const d = new Date(t.data);
        return d.getMonth() === m && d.getFullYear() === y;
      })
      .reduce((sum, t) => sum + Number(t.valor || 0), 0);
  }, [allTransactions, selectedDate, accounts]);

  const recentTransactions = useMemo(() => {
    return (allTransactions || [])
      .filter((t) => !t.ignorar_transacao)
      .slice(0, 5);
  }, [allTransactions]);

  // Filtro de busca para a aba de transações
  const filteredBySearch = (items: Transaction[]) => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase().trim();
    
    // Se a busca for exatamente o nome de uma categoria, filtramos apenas por ela
    const isCategoryExactMatch = categories.some(cat => cat.nome.toLowerCase() === q);

    return items.filter((t: Transaction) => {
      const desc = (t.descricao || "").toLowerCase();
      // Dados retornados do Supabase incluem "categoria", que não está no tipo Transaction.
      // Usar cast local para acessar com segurança sem alterar o tipo global.
      const cat = (((t as unknown as { categoria?: { nome?: string } }).categoria?.nome) || "").toLowerCase();
      const valorStr = String(t.valor ?? "");
      const valorCurrency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(t.valor || 0).toLowerCase();
      
      if (isCategoryExactMatch) {
        return cat === q;
      }
      
      return desc.includes(q) || cat.includes(q) || valorStr.includes(q) || valorCurrency.includes(q);
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if user is not authenticated
  if (!user) {
    return null;
  }

  const renderCurrentTab = () => {
    switch (currentTab) {
      case "goals":
        return <GoalsPage />;
      case "reports":
        return (
          <Reports
            transactions={allTransactions}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            initialActiveReport={reportsInitialActive}
            categories={categories}
            onNavigateToTransactions={(categoryFilter, month, year) => {
              setCurrentTab("transactions");
              if (month !== undefined && year !== undefined) {
                setSelectedDate(new Date(year, month));
              }
              if (categoryFilter) {
                setSearchQuery(categoryFilter);
                setSearchOpen(true);
              }
            }}
          />
        );
      case "accounts":
        return <Accounts />;
      case "categories":
        return <Categories />;
      case "credit-cards":
        return (
          <div className="p-3 sm:p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
              <CreditCardManager />
            </div>
          </div>
        );
      case "transactions": {
        // Filter allTransactions by effective date instead of relying on the DB 'data' filter
        const transactionsForCurrentMonth = allTransactions.filter(t => {
          const transactionDate = getEffectiveTransactionDate(t, accounts);
          if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
          
          const selectedMonth = selectedDate.getMonth();
          const selectedYear = selectedDate.getFullYear();
          return !t.ignorar_transacao && transactionDate.getMonth() === selectedMonth && transactionDate.getFullYear() === selectedYear;
        });

        // Apply type filter
        const typeFilteredTransactions = typeFilter === 'todos' 
          ? transactionsForCurrentMonth 
          : transactionsForCurrentMonth.filter(t => t.tipo === typeFilter);

        const currentTransactions = filteredBySearch(typeFilteredTransactions);
        
        // Calcular totais com base em todas as transações do período, não apenas as filtradas
        const totalIncome = transactionsForCurrentMonth
          .filter(t => t.tipo === 'entrada' && t.foi_paga)
          .reduce((sum, t) => sum + Number(t.valor), 0);
          
        const totalExpenses = transactionsForCurrentMonth
          .filter(t => t.tipo === 'saida')
          .reduce((sum, t) => sum + Number(t.valor), 0);
          
        const balance = totalIncome - totalExpenses;

        return (
          <div className="p-3 sm:p-4 md:p-6">
            <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
              {/* Container Principal */}
              <Card className="bg-card border-border">
                <CardContent className="p-4 sm:p-6">
                  {/* Card Único de Resumo das Transações */}
                  <Card className="bg-card border-border mb-6">
                    <CardContent className="p-4 sm:p-6">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Receitas */}
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="p-2 rounded-md bg-green-500/10 text-green-600 dark:bg-green-500/20 dark:text-green-400">
                                <TrendingUp className="h-4 w-4" />
                              </div>
                              <h3 className="text-sm font-medium text-muted-foreground">
                                Receitas
                              </h3>
                            </div>
                            <div className="text-lg font-bold text-green-600 dark:text-green-400">
                              {formatCurrency(totalIncome)}
                            </div>
                          </div>
                        </div>

                        {/* Despesas */}
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="p-2 rounded-md bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400">
                                <TrendingDown className="h-4 w-4" />
                              </div>
                              <h3 className="text-sm font-medium text-muted-foreground">
                                Despesas
                              </h3>
                            </div>
                            <div className="text-lg font-bold text-red-600 dark:text-red-400">
                              {formatCurrency(totalExpenses)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Seletor de Data Centralizado */}
                  <div className="flex justify-center mb-6">
                    <MonthYearPicker
                      selectedDate={selectedDate}
                      onDateChange={handleDateChange}
                    />
                  </div>

                  <TransactionTable
                    transactions={currentTransactions}
                    selectedDate={selectedDate}
                    onTransactionUpdate={() => fetchTransactions(selectedDate, typeFilter)}
                    typeFilter={typeFilter}
                    onTypeFilterChange={(filter) => {
                      setTypeFilter(filter);
                      fetchTransactions(selectedDate, filter);
                    }}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        );
      }
      case "settings":
        return (
          <div className="p-3 sm:p-4 md:p-6">
            <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
              <CategoryManager onCategoryUpdate={() => fetchTransactions(selectedDate, typeFilter)} />
              <AccountManager onAccountUpdate={() => fetchTransactions(selectedDate, typeFilter)} />
            </div>
          </div>
        );

      default:
        // Renderização específica para mobile na dashboard
        if (isMobile) {
          return (
            <div className="px-4 py-2">
              <MobileDashboardHeader
                onTransactionSuccess={handleRefresh}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                onBalanceClick={() => setCurrentTab("accounts")}
              />
              <MobileSummaryCards
                income={income}
                expenses={expenses}
                onIncomeClick={() => {
                  setCurrentTab("transactions");
                  setTypeFilter("entrada");
                  fetchTransactions(selectedDate, "entrada");
                }}
                onExpenseClick={() => {
                  setCurrentTab("transactions");
                  setTypeFilter("saida");
                  fetchTransactions(selectedDate, "saida");
                }}
                isLoading={refreshing}
              />
              <div className="space-y-6">
                {/* Card de Despesas Pendentes */}
                <div className="bg-card border border-border rounded-xl p-4 hover:bg-accent/30 hover:shadow-medium hover:-translate-y-1 transition-all duration-300 cursor-pointer shadow-soft"
                  onClick={() => {
                    setCurrentTab("reports");
                    setReportsInitialActive('pending');
                  }}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 bg-red-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icons.AlertCircle className="h-4 w-4 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">Despesas Pendentes</h3>
                      <p className="text-base font-semibold text-foreground">Total a Pagar</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-red-600">
                        R$ {pendingSumThisMonth.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-sm text-muted-foreground">Mês atual</span>
                      <span className="text-sm font-medium text-foreground">
                        {(() => {
                          const count = (allTransactions || [])
                            .filter((t) => t.tipo === 'saida' && !t.foi_paga && !String(t.id).startsWith('virtual-'))
                            .filter((t) => {
                              if (t.conta_id) {
                                const account = accounts.find(a => a.id === t.conta_id);
                                if (account && account.tipo === 'cartao_credito') return false;
                              }
                              const d = new Date(t.data);
                              return d.getMonth() === selectedDate.getMonth() && d.getFullYear() === selectedDate.getFullYear();
                            }).length;
                          return `${count} ${count === 1 ? 'despesa' : 'despesas'}`;
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <span className="text-sm font-medium text-red-600">
                        {pendingSumThisMonth > 0 ? 'Pendente' : 'Em dia'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card de Cartões de Crédito */}
                <div className="bg-card border border-border rounded-xl p-4 hover:bg-accent/30 hover:shadow-medium hover:-translate-y-1 transition-all duration-300 cursor-pointer shadow-soft"
                  onClick={() => setCurrentTab("credit-cards")}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icons.CreditCard className="h-4 w-4 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">Cartões de Crédito</h3>
                      <p className="text-base font-semibold text-foreground">Limite Disponível</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-foreground">
                        R$ {(creditCardsData.totalLimit - creditCardsData.totalUsed).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-sm text-muted-foreground">Fatura Atual</span>
                      <span className="text-sm font-medium text-foreground">
                        R$ {creditCardsData.totalUsed.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-sm text-muted-foreground">Cartões ativos</span>
                      <span className="text-sm font-medium text-foreground">
                        {creditCardsData.cardsCount} {creditCardsData.cardsCount === 1 ? 'cartão' : 'cartões'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card da Meta Principal */}
                {mainGoal && (() => {
                  const progress = (mainGoal.valor_atual / mainGoal.valor_meta) * 100;
                  const getProgressColor = (progress: number) => {
                    if (progress < 50) return 'bg-red-500';
                    if (progress < 70) return 'bg-yellow-500';
                    if (progress < 90) return 'bg-purple-500';
                    return 'bg-green-500';
                  };

                  return (
                    <div className="bg-card border border-border rounded-xl p-4 hover:bg-accent/30 hover:shadow-medium hover:-translate-y-1 transition-all duration-300 cursor-pointer shadow-soft"
                      onClick={() => setCurrentTab("goals")}>
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-8 h-8 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                          {(() => {
                            const iconsRecord = Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>;
                            const IconComponent = iconsRecord[mainGoal.icone] || iconsRecord.Target;
                            return <IconComponent className="h-4 w-4 text-green-500" />;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-muted-foreground mb-1">Meta Principal</h3>
                          <p className="text-base font-semibold text-foreground truncate">{mainGoal.titulo}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm text-muted-foreground">Faltam</div>
                          <div className="text-sm font-semibold text-foreground">
                            R$ {Math.max(0, mainGoal.valor_meta - mainGoal.valor_atual).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Progresso</span>
                          <span className="text-sm font-bold text-foreground">
                            {progress.toFixed(0)}%
                          </span>
                        </div>

                        <div className="relative">
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(progress)}`}
                              style={{
                                width: `${Math.min(progress, 100)}%`
                              }}
                            />
                          </div>
                        </div>

                        <div className="flex justify-between items-center text-sm">
                          <div>
                            <div className="text-sm text-muted-foreground">Atual</div>
                            <div className="font-semibold text-foreground text-sm">
                              R$ {mainGoal.valor_atual.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">Meta</div>
                            <div className="font-semibold text-foreground text-sm">
                              R$ {mainGoal.valor_meta.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <Suspense fallback={
                  <div className="w-full h-64 flex items-center justify-center">
                    <div className="animate-pulse flex space-x-4">
                      <div className="flex-1 space-y-4 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 rounded"></div>
                          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                }>
                  <DashboardCharts
                    transactions={allTransactions}
                    accounts={accounts}
                    selectedDate={selectedDate}
                    onViewMore={(chartType) => {
                      setCurrentTab("reports");
                      if (chartType === 'expenses-by-category') {
                        setReportsInitialActive('category-expenses');
                      } else if (chartType === 'income-by-category') {
                        setReportsInitialActive('category-income');
                      } else if (chartType === 'spending-frequency') {
                        setReportsInitialActive('monthly-expenses');
                      } else if (chartType === 'monthly-income') {
                        setReportsInitialActive('monthly-income');
                      } else if (chartType === 'monthly-expenses') {
                        setReportsInitialActive('monthly-expenses');
                      } else if (chartType === 'balance-6-months') {
                        setReportsInitialActive('balance');
                      } else if (chartType === 'annual-balance') {
                        setReportsInitialActive('cash-flow');
                      } else if (chartType === 'revenue-vs-expenses') {
                        setReportsInitialActive('balance');
                      } else if (chartType === 'monthly-balance') {
                        setReportsInitialActive('monthly-balance');
                      } else if (chartType === 'net-worth-evolution') {
                        setReportsInitialActive('net-worth');
                      } else {
                        setReportsInitialActive('monthly');
                      }
                    }}
                  />
                </Suspense>

              </div>
            </div>
          );
        }

        // Layout desktop
        return (
          <div className="p-3 sm:p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
              <Card>
                <CardContent className="p-6 space-y-4 sm:space-y-6">
                  {/* Grid responsivo para cards financeiros - 4 cards na ordem solicitada */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    <FinanceCard
                      title="Saldo Total"
                      value={totalBalance}
                      type="balance"
                      onClick={() => setCurrentTab("accounts")}
                    />
                    <FinanceCard
                      title="Receitas"
                      value={income}
                      type="income"
                      onClick={() => {
                        setCurrentTab("transactions");
                        setTypeFilter("entrada");
                        fetchTransactions(selectedDate, "entrada");
                      }}
                    />
                    <FinanceCard
                      title="Despesas"
                      value={expenses}
                      type="expense"
                      onClick={() => {
                        setCurrentTab("transactions");
                        setTypeFilter("saida");
                        fetchTransactions(selectedDate, "saida");
                      }}
                    />
                    <FinanceCard
                      title="Despesas pendentes"
                      value={pendingSumThisMonth}
                      type="pending"
                      onClick={() => {
                        setCurrentTab("reports");
                        setReportsInitialActive('pending');
                      }}
                    />
                  </div>

                  {/* Card da Meta Principal */}
                  {mainGoal && (() => {
                    const progress = (mainGoal.valor_atual / mainGoal.valor_meta) * 100;

                    // Calcular meses restantes baseado na data de criação
                    const dataCriacao = new Date(mainGoal.criado_em);
                    const dataAtual = new Date();
                    const mesesPassados = differenceInMonths(dataAtual, dataCriacao);
                    const mesesRestantes = Math.max(0, mainGoal.prazo_meses - mesesPassados);

                    const getProgressColor = (progress: number) => {
                      if (progress < 50) return 'bg-red-500';
                      if (progress < 70) return 'bg-yellow-500';
                      if (progress < 90) return 'bg-purple-500';
                      return 'bg-green-500';
                    };

                    return (
                      <div className="bg-card border border-border rounded-xl p-4 sm:p-6 hover:bg-accent/30 hover:shadow-medium transition-all duration-300 cursor-pointer shadow-soft"
                        onClick={() => setCurrentTab("goals")}>
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            {(() => {
                              const iconsRecord = Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>;
                              const IconComponent = iconsRecord[mainGoal.icone] || iconsRecord.Target;
                              return <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />;
                            })()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-1">Meta Principal</h3>
                            <p className="text-base font-semibold text-foreground truncate">{mainGoal.titulo}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-muted-foreground">Faltam</div>
                            <div className="text-xs sm:text-sm font-semibold text-foreground">
                              R$ {Math.max(0, mainGoal.valor_meta - mainGoal.valor_atual).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {mesesRestantes} {mesesRestantes === 1 ? 'mês' : 'meses'} restante{mesesRestantes !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-xs sm:text-sm text-muted-foreground">Progresso</span>
                            <span className="text-base font-bold text-foreground">
                              {progress.toFixed(0)}%
                            </span>
                          </div>

                          <div className="relative">
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div
                                className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(progress)}`}
                                style={{
                                  width: `${Math.min(progress, 100)}%`
                                }}
                              />
                            </div>
                          </div>

                          <div className="flex justify-between items-center text-xs sm:text-sm">
                            <div>
                              <div className="text-xs text-muted-foreground">Atual</div>
                              <div className="font-semibold text-foreground text-xs sm:text-sm">
                                R$ {mainGoal.valor_atual.toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-muted-foreground">Meta</div>
                              <div className="font-semibold text-foreground text-xs sm:text-sm">
                                R$ {mainGoal.valor_meta.toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Seletor de Data */}
                  <div className="flex justify-center">
                    <MonthYearPicker
                      selectedDate={selectedDate}
                      onDateChange={handleDateChange}
                    />
                  </div>

                  {/* Seção de Gráficos com layout responsivo */}
                  <div className="space-y-4 sm:space-y-6">
                    <DashboardCharts
                      transactions={allTransactions}
                      accounts={accounts}
                      selectedDate={selectedDate}
                      onViewMore={(chartType) => {
                        setCurrentTab("reports");
                        if (chartType === 'expenses-by-category') {
                          setReportsInitialActive('category-expenses');
                        } else if (chartType === 'income-by-category') {
                          setReportsInitialActive('category-income');
                        } else if (chartType === 'spending-frequency') {
                          setReportsInitialActive('monthly-expenses');
                        } else if (chartType === 'monthly-income') {
                          setReportsInitialActive('monthly-income');
                        } else if (chartType === 'monthly-expenses') {
                          setReportsInitialActive('monthly-expenses');
                        } else if (chartType === 'balance-6-months') {
                          setReportsInitialActive('balance');
                        } else if (chartType === 'annual-balance') {
                          setReportsInitialActive('balance');
                        } else if (chartType === 'revenue-vs-expenses') {
                          setReportsInitialActive('balance');
                        } else if (chartType === 'monthly-balance') {
                          setReportsInitialActive('monthly-balance');
                        } else if (chartType === 'net-worth-evolution') {
                          setReportsInitialActive('net-worth');
                        } else {
                          setReportsInitialActive('monthly');
                        }
                      }}
                      onNavigateToTransactions={(month, year) => {
                        const targetDate = new Date(year, month, 1);
                        setSelectedDate(targetDate);
                        setCurrentTab("transactions");
                        setTypeFilter("todos");
                        fetchTransactions(targetDate, "todos");
                      }}
                    />
                  </div>

                </CardContent>
              </Card>
            </div>
          </div>
        );
    }
  };

  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col w-full bg-background overflow-x-hidden">
        {currentTab === 'dashboard' && <UserProfileHeader />}
        {/* Conteúdo principal para mobile */}
        <div className={`flex-1 overflow-auto pb-20 ${currentTab === 'dashboard' ? 'pt-24' : 'pt-4'}`}>
          {renderCurrentTab()}
        </div>

        {/* Bottom Navigation para mobile */}
        <BottomNavigation 
          currentTab={currentTab} 
          onTabChange={handleTabChange}
          onTransactionSuccess={handleRefresh}
          onOpenAssistant={() => setAssistantOpen(true)}
        />
        <LocalAIAssistant
          onTransactionSuccess={handleRefresh}
          open={assistantOpen}
          onOpenChange={(value) => {
            setAssistantOpen(value);
          }}
          showTrigger={false}
          autoStartListeningOnOpen={false}
        />
        <MicPermissionModal />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background overflow-x-hidden">
        <AppSidebar
          currentTab={currentTab}
          onTabChange={handleTabChange}
          onTransactionSuccess={() => fetchTransactions(selectedDate, typeFilter)}
          onOpenAssistant={() => setAssistantOpen(true)}
        />
        <SidebarInset className="flex-1 flex flex-col">
          <DesktopTopbar currentTab={currentTab} />
          <div className="flex-1 overflow-auto">
            {renderCurrentTab()}
          </div>
        </SidebarInset>
        <LocalAIAssistant
          onTransactionSuccess={handleRefresh}
          open={assistantOpen}
          onOpenChange={(value) => {
            setAssistantOpen(value);
          }}
          showTrigger={false}
          autoStartListeningOnOpen={false}
          startListeningSignal={0}
          forceStopListeningSignal={0}
        />
        <MicPermissionModal />
      </div>
    </SidebarProvider>
  );
};

export default Index;
